/**
 * Runtime License API cục bộ của bản cài on-premise (ADR-0058 mục 6.1;
 * `libs/license-runtime` README): trạng thái license, nhập tệp, tải
 * `activation-request.json`. Đường `/internal/v1/license*` không đi qua Gateway
 * của Control Plane; ở máy dev, Vite chuyển nó tới `LICENSE_RUNTIME_TARGET`.
 *
 * Mỗi hàm chọn API thật hay dữ liệu mẫu bằng `dungApiThat()` lúc gọi. Mock từ
 * chối giống License Agent (tệp không phải JWS, quá 64 KB) để giao diện chạy
 * được nhánh lỗi trước khi có máy chủ thật.
 */
import { dungApiThat, goiApi, goiApiTep } from '../api/http';
import type { ApiError } from '../errors';
import { CAU_LOI_MOCK_CUC_BO, MOCK_ACTIVATION_REQUEST, trangThaiMau } from '../../mock/license-cuc-bo';
import { mockRequest, mockState } from '../../mock/transport';
import { doanLoaiTep, TEP_TOI_DA_BYTE } from '../../modules/license-cuc-bo/nghiep-vu/license-cuc-bo';
import type { LicenseCucBo, LoaiTepNhap, TrangThaiCucBo } from '../../modules/license-cuc-bo/nghiep-vu/types';

const GOC = '/internal/v1/license';

const TRANG_THAI_MAU: readonly TrangThaiCucBo[] = [
  'ACTIVE',
  'EXPIRING',
  'GRACE',
  'READ_ONLY',
  'NOT_YET_VALID',
  'INVALID',
];

/** Nhập license mẫu xong thì trạng thái mẫu thành `ACTIVE`, như máy chủ thật. */
let daNhap: TrangThaiCucBo | null = null;

/** Trạng thái mẫu chọn bằng `?license=`; `?mock=empty` là chưa nhập license nào. */
function trangThaiMockHienTai(): LicenseCucBo {
  if (mockState() === 'empty') return trangThaiMau('INVALID');
  const thamSo = new URLSearchParams(window.location.search).get('license')?.toUpperCase();
  const chon = TRANG_THAI_MAU.find((s) => s === thamSo);
  return trangThaiMau(chon ?? daNhap ?? 'ACTIVE');
}

const loi = (code: string, status: number, message: string): Promise<never> => {
  const e: ApiError = { code, status, message };
  return Promise.reject(e);
};

export const licenseCucBoRepository = {
  trangThai(): Promise<LicenseCucBo> {
    if (dungApiThat()) return goiApi<LicenseCucBo>(GOC);
    return mockRequest(trangThaiMockHienTai());
  },

  /** Nhập license, keyring hoặc danh sách thu hồi; trả trạng thái mới. */
  async nhapTep(loai: LoaiTepNhap, tep: Blob): Promise<LicenseCucBo> {
    if (dungApiThat()) {
      return goiApi<LicenseCucBo>(`${GOC}/import`, {
        method: 'POST',
        query: { type: loai },
        thanTho: { duLieu: tep, kieu: 'application/octet-stream' },
      });
    }
    const noiDung = await tep.text();
    if (tep.size > TEP_TOI_DA_BYTE) {
      return loi('LICENSE_FORMAT_INVALID', 413, CAU_LOI_MOCK_CUC_BO.tepSai);
    }
    const loaiTrongTep = doanLoaiTep(noiDung);
    if (loaiTrongTep === null || loaiTrongTep !== loai) {
      return loi('LICENSE_FORMAT_INVALID', 422, CAU_LOI_MOCK_CUC_BO.tepSai);
    }
    if (loai === 'license') daNhap = 'ACTIVE';
    return mockRequest(trangThaiMockHienTai());
  },

  /** `activation-request.json` của bản cài, đúng từng byte tệp đã lưu. */
  taiYeuCauKichHoat(): Promise<Blob> {
    if (dungApiThat()) return goiApiTep(`${GOC}/activation-request`, 'application/json, application/problem+json');
    return mockRequest(new Blob([MOCK_ACTIVATION_REQUEST], { type: 'application/json' }));
  },
};
